import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../lib/api';
import { Plus, Trash2, KeyRound, Copy, RefreshCw, X, CheckCircle, ShieldAlert } from 'lucide-react';

function SecretBanner({ secret, onClose }: { secret: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
      <p className="text-sm text-green-800 font-medium mb-1">Secret key created — copy it now, it won't be shown again:</p>
      <div className="flex items-center gap-2">
        <code className="text-xs bg-white px-2 py-1 rounded border break-all flex-1">{secret}</code>
        <button onClick={() => { navigator.clipboard.writeText(secret); setCopied(true); setTimeout(() => setCopied(false), 1500); }} className="text-green-700">
          {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>
        <button onClick={onClose} className="text-gray-400"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function KeyModal({ appId, scopes, adminScopes, onClose, onIssued }: { appId: string; scopes: string[]; adminScopes: string[]; onClose: () => void; onIssued: (secret: string) => void }) {
  const [selected, setSelected] = useState<string[]>([]);
  const [name, setName] = useState('');
  const issue = useMutation({
    mutationFn: () => adminAPI.issueApiKey(appId, selected, name || undefined),
    onSuccess: (d) => onIssued(d.secret),
  });
  const toggle = (s: string) => setSelected((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[85vh] overflow-auto">
        <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-semibold">New API key</h2><button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button></div>
        <input placeholder="Key name (optional)" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4" />
        {adminScopes.length > 0 && (
          <div className="mb-4 border border-amber-200 bg-amber-50 rounded-md p-3">
            <p className="text-sm font-medium text-amber-800 flex items-center mb-2"><ShieldAlert className="w-4 h-4 mr-1" />Admin-tier (system-wide)</p>
            {adminScopes.map((s) => (
              <label key={s} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={selected.includes(s)} onChange={() => toggle(s)} /> <code>{s}</code> <span className="text-xs text-amber-700">— full platform access</span></label>
            ))}
          </div>
        )}
        <p className="text-sm font-medium text-gray-700 mb-2">Org scopes</p>
        <div className="grid grid-cols-2 gap-1 mb-4">
          {scopes.map((s) => (
            <label key={s} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={selected.includes(s)} onChange={() => toggle(s)} /> <code className="text-xs">{s}</code></label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md">Cancel</button>
          <button onClick={() => issue.mutate()} disabled={selected.length === 0 || issue.isLoading} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">Create key</button>
        </div>
      </div>
    </div>
  );
}

function AppKeys({ appId, onAddKey, onSecret }: { appId: string; onAddKey: () => void; onSecret: (s: string) => void }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin-app', appId], queryFn: () => adminAPI.getApp(appId) });
  const rotate = useMutation({ mutationFn: (keyId: string) => adminAPI.rotateApiKey(appId, keyId), onSuccess: (d) => { onSecret(d.secret); qc.invalidateQueries({ queryKey: ['admin-app', appId] }); } });
  const revoke = useMutation({ mutationFn: (keyId: string) => adminAPI.revokeApiKey(appId, keyId), onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-app', appId] }) });
  const keys = data?.keys || [];
  return (
    <div className="mt-4 border-t pt-4">
      <div className="flex justify-between items-center mb-2">
        <p className="text-sm font-medium text-gray-700">Secret keys</p>
        <button onClick={onAddKey} className="text-sm text-blue-600">+ New key</button>
      </div>
      {keys.length === 0 ? <p className="text-sm text-gray-500">No keys yet.</p> : (
        <div className="space-y-2">
          {keys.map((k: any) => (
            <div key={k.keyId} className="flex items-center justify-between text-sm">
              <div>
                <code className="text-xs">sk_live_{k.keyId.slice(0, 8)}…</code>
                <span className={`text-xs ml-2 ${(k.scopes || []).includes('admin:*') ? 'text-amber-600 font-medium' : 'text-gray-500'}`}>{(k.scopes || []).join(', ')}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => rotate.mutate(k.keyId)} title="Rotate" className="text-gray-500 hover:text-amber-600"><RefreshCw className="w-4 h-4" /></button>
                <button onClick={() => confirm('Revoke this key?') && revoke.mutate(k.keyId)} title="Revoke" className="text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ApiKeysPage: React.FC = () => {
  const qc = useQueryClient();
  const { data: apps = [] } = useQuery({ queryKey: ['admin-apps'], queryFn: () => adminAPI.getApps() });
  const { data: scopeData } = useQuery({ queryKey: ['admin-key-scopes'], queryFn: () => adminAPI.getApiKeyScopes() });
  const [newName, setNewName] = useState('');
  const [keyModalApp, setKeyModalApp] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const create = useMutation({ mutationFn: () => adminAPI.createApp(newName), onSuccess: () => { setNewName(''); qc.invalidateQueries({ queryKey: ['admin-apps'] }); } });
  const del = useMutation({ mutationFn: (id: string) => adminAPI.deleteApp(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-apps'] }) });

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">API Keys</h1>
      <p className="text-gray-600 mb-6">Mint programmatic keys for the platform API. As a super-admin you can issue <strong>admin-tier</strong> keys (system-wide) or org-scoped keys.</p>

      {secret && <SecretBanner secret={secret} onClose={() => setSecret(null)} />}

      <div className="flex gap-2 mb-6">
        <input placeholder="New application name" value={newName} onChange={(e) => setNewName(e.target.value)} className="border border-gray-300 rounded-md px-3 py-2 flex-1 max-w-sm" />
        <button onClick={() => newName && create.mutate()} className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center"><Plus className="w-4 h-4 mr-1" />Create app</button>
      </div>

      <div className="space-y-4">
        {apps.length === 0 && <p className="text-gray-500">No applications yet. Create one to issue keys.</p>}
        {apps.map((a: any) => (
          <div key={a.id} className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold text-gray-900">{a.name}</p>
                <p className="text-xs text-gray-500 mt-1">Publishable key</p>
                <code className="text-xs bg-gray-50 px-2 py-1 rounded border break-all">{a.publishableKey}</code>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setExpanded(expanded === a.id ? null : a.id)} className="text-sm text-blue-600 flex items-center"><KeyRound className="w-4 h-4 mr-1" />Keys</button>
                <button onClick={() => confirm('Delete app and its keys?') && del.mutate(a.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            {expanded === a.id && <AppKeys appId={a.id} onAddKey={() => setKeyModalApp(a.id)} onSecret={setSecret} />}
          </div>
        ))}
      </div>

      {keyModalApp && (
        <KeyModal
          appId={keyModalApp}
          scopes={scopeData?.scopes || []}
          adminScopes={scopeData?.adminScopes || []}
          onClose={() => setKeyModalApp(null)}
          onIssued={(s) => { setKeyModalApp(null); setSecret(s); }}
        />
      )}
    </div>
  );
};

export default ApiKeysPage;
